export const TEMPLATE_ADMIN = 'admin';
export const TEMPLATE_GENERAL_INTERNAL = 'general_internal';
export const TEMPLATE_TASK_ONLY_INTERNAL = 'task_only_internal';
export const TEMPLATE_TASK_ONLY_EXTERNAL = 'task_only_external';
export const TEMPLATE_EXTERNAL_GENERAL = 'external_general';

export const SCOPE_ALL = 'all';
export const SCOPE_ASSIGNED_ONLY = 'assigned_only';
export const SCOPE_LINKED_VISIBLE_EVENTS_ONLY = 'linked_visible_events_only';
export const SCOPE_SELECTED_EVENTS_ONLY = 'selected_events_only';

// AIチューニングなど、権限配列へ委譲せずDB上のraw admin roleだけで守る導線用。
export const hasExactAdminRole = (role) =>
  String(role || '').trim().toLowerCase() === 'admin';

export const EVENT_SCOPE_OPTIONS = Object.freeze([
  { value: SCOPE_ALL, label: '全件' },
  { value: SCOPE_ASSIGNED_ONLY, label: '担当イベントのみ' },
  { value: SCOPE_SELECTED_EVENTS_ONLY, label: '選択イベントのみ' },
]);

export const ARTIST_SCOPE_OPTIONS = Object.freeze([
  { value: SCOPE_ALL, label: '全件' },
  { value: SCOPE_LINKED_VISIBLE_EVENTS_ONLY, label: '閲覧可能イベントに紐づくアーティストのみ' },
  { value: SCOPE_SELECTED_EVENTS_ONLY, label: '選択イベントに紐づくアーティストのみ' },
]);

export const EXTERNAL_ACCOUNT_ABSOLUTE_DENY_KEYS = Object.freeze([
  'NAV_ACTION_LOGS',
  'CAN_VIEW_ACTION_LOGS',
  'NAV_POLICIES',
  'CAN_EDIT_POLICIES',
  'NAV_ACCOUNT_MANAGEMENT',
  'SYSTEM_MANAGE_ACCOUNTS',
  'SYSTEM_MANAGE_INVITES',
  'NAV_OPERATIONAL_EMAILS',
  'SYSTEM_SEND_OPERATIONAL_EMAILS',
  'SYSTEM_MANAGE_MOBILE_RELEASES',
  'SYSTEM_MANAGE_MAC_RELEASES',
  'NAV_CONCIERGE',
  'CAN_MANAGE_CONCIERGE',
  'NAV_MUSIC_LIBRARY',
  'MUSIC_VIEW_LIBRARY',
  'MUSIC_CREATE_WORK',
  'MUSIC_EDIT_WORK',
  'MUSIC_DELETE_WORK',
  'MUSIC_CREATE_PERSON',
  'MUSIC_EDIT_PERSON',
  'MUSIC_MERGE_ENTITIES',
  'MUSIC_EDIT_RELATIONSHIPS',
  'MUSIC_IMPORT_SEED_DATA',
  'MUSIC_VIEW_SOURCES',
  'MUSIC_UPLOAD_SCORE',
  'MUSIC_DOWNLOAD_SCORE',
  'MUSIC_VIEW_RIGHTS',
  'MUSIC_EDIT_RIGHTS',
  'MUSIC_UPLOAD_AUDIO',
  'MUSIC_PLAY_AUDIO',
  'MUSIC_SHARE_SCORE',
  'MUSIC_VIEW_SHARE_LOGS',
]);

const EXTERNAL_ACCOUNT_ABSOLUTE_DENY_KEY_SET = new Set(EXTERNAL_ACCOUNT_ABSOLUTE_DENY_KEYS);

const entry = (key, label, description, extra = {}) => ({
  key,
  label,
  description,
  externalForbidden:
    extra.externalForbidden === true ||
    EXTERNAL_ACCOUNT_ABSOLUTE_DENY_KEY_SET.has(String(key || '').trim()),
  ...extra,
});
const EVENT_SCOPED_EXTRA = Object.freeze({
  supportsScope: true,
  scopeKind: 'event',
  scopeOptions: EVENT_SCOPE_OPTIONS,
});

const ARTIST_SCOPED_EXTRA = Object.freeze({
  supportsScope: true,
  scopeKind: 'artist_event',
  scopeOptions: ARTIST_SCOPE_OPTIONS,
});

export const ADMIN_PERMISSION_GROUPS = Object.freeze([
  {
    key: 'navigation',
    title: '画面アクセス',
    items: [
      entry('NAV_ARTISTS', 'アーティスト管理', 'アーティスト一覧・関連画面へ入れます。', { section: 'view' }),
      entry('NAV_EVENTS', 'イベント一覧', 'イベント一覧画面へ入れます。', { section: 'view' }),
      entry('NAV_EVENT_REGISTER', 'イベント登録', 'イベント登録画面へ入れます。', { section: 'edit' }),
      entry('NAV_VENUES', '会場管理', '会場一覧・会場登録へ入れます。', { section: 'view' }),
      entry('NAV_MUSIC_LIBRARY', '楽曲管理', '楽曲管理画面へ入れます。', { section: 'view' }),
      entry('NAV_EVENT_CHAT', 'イベントチャット', 'イベントチャットへ入れます。', { section: 'view' }),
      entry('NAV_GROUP_CHAT', 'グループチャット', 'グループチャットへ入れます。', { section: 'view' }),
      entry('NAV_PERSONAL_CHAT', '個別チャット', '個別チャットへ入れます。', { section: 'view' }),
      entry('NAV_CHAT_BROADCAST', '一斉送信', 'アーティスト向け一斉送信へ入れます。', { section: 'execute' }),
      entry('NAV_CASTING_OFFER', 'オファー管理', 'オファー履歴・申請へ入れます。', { section: 'view', ...EVENT_SCOPED_EXTRA }),
      entry('NAV_CASTING_CONTRACT', '本契約管理', '本契約履歴・申請へ入れます。', { section: 'view', ...EVENT_SCOPED_EXTRA }),
      entry('NAV_CONCIERGE', 'コンシェルジュ', 'コンシェルジュ画面へ入れます。', { section: 'view' }),
      entry('NAV_TICKET_SALES', 'チケット売上', 'チケット売上画面へ入れます。', { section: 'view' }),
      entry('NAV_FINANCE', '経理・集計', '経理・集計画面へ入れます。', { section: 'view' }),
      entry('NAV_PAYMENTS', '請求一覧', '請求一覧画面へ入れます。', { section: 'view', ...EVENT_SCOPED_EXTRA }),
      entry('NAV_TASKS', 'タスク', 'タスクダッシュボードへ入れます。', { section: 'view', ...EVENT_SCOPED_EXTRA }),
      entry('NAV_ACTION_LOGS', 'アクションログ', 'アクションログ画面へ入れます。', { section: 'view', sensitivity: 'system' }),
      entry('NAV_ANNOUNCEMENTS', 'お知らせ管理', 'お知らせ管理画面へ入れます。', { section: 'view' }),
      entry('NAV_OPERATIONAL_EMAILS', 'メール配信', '運用連絡メール配信画面へ入れます。', { section: 'execute', sensitivity: 'system' }),
      entry('NAV_POLICIES', '規約管理', '規約管理画面へ入れます。', { section: 'view', sensitivity: 'system' }),
      entry('NAV_ACCOUNT_MANAGEMENT', 'アカウント管理', '管理者アカウント管理画面へ入れます。', { section: 'view', sensitivity: 'system' }),
      entry('NAV_BUGS', 'バグ管理', 'バグ管理画面へ入れます。', { section: 'view' }),
      entry('NAV_AI_PLANNER', 'Casticart AI', 'Casticart AIとの会話画面を利用できます。初期状態はOFFで、対象者へ個別に付与します。AI設定の変更権限は含みません。', { section: 'view' }),
      entry('NAV_SURVEYS', 'アンケート管理', 'イベント詳細からアンケート管理画面へ入れます。原票・個人情報・出力の個別権限は含みません。', { section: 'view', sensitivity: 'private' }),
    ],
  },
  {
    key: 'surveys',
    title: 'アンケート管理',
    items: [
      entry('SURVEY_VIEW', '取込・回答を表示', '個人情報を除く取込、回答、集計を表示できます。', { section: 'view', sensitivity: 'private', ...EVENT_SCOPED_EXTRA }),
      entry('SURVEY_IMPORT', '原票取込・AI読取', '原票アップロード、設問確定、AI読取を実行できます。', { section: 'execute', sensitivity: 'private', ...EVENT_SCOPED_EXTRA }),
      entry('SURVEY_SOURCE_VIEW', '原票を表示', '保護された原票画像・PDFを表示できます。', { section: 'view', sensitivity: 'private', ...EVENT_SCOPED_EXTRA }),
      entry('SURVEY_SOURCE_DOWNLOAD', '原票を保存', '保護された原票をダウンロードできます。', { section: 'execute', sensitivity: 'private', ...EVENT_SCOPED_EXTRA }),
      entry('SURVEY_REVIEW', '読取・顧客候補を確認', '曖昧回答と顧客候補を人が確認できます。', { section: 'edit', sensitivity: 'private', ...EVENT_SCOPED_EXTRA }),
      entry('SURVEY_FINALIZE', '集計・まとめを確定', '集計を固定し詳細まとめを作成できます。', { section: 'execute', sensitivity: 'private', ...EVENT_SCOPED_EXTRA }),
      entry('SURVEY_EXPORT', 'XLSXを出力', '確認済み回答をXLSXで出力できます。', { section: 'execute', sensitivity: 'private', ...EVENT_SCOPED_EXTRA }),
      entry('SURVEY_DELETE', '原票・取込を削除', '保持期限の更新と原票削除を実行できます。', { section: 'delete', sensitivity: 'system', ...EVENT_SCOPED_EXTRA }),
      entry('SURVEY_FORM_EDIT', 'デジタルアンケートを編集', 'イベント内のアンケート作成、設問編集、下書き保存を実行できます。', { section: 'edit', sensitivity: 'private', ...EVENT_SCOPED_EXTRA }),
      entry('SURVEY_PUBLISH', 'デジタルアンケートを公開', '回答受付の公開、公開版の更新、配布経路の作成を実行できます。', { section: 'execute', sensitivity: 'private', ...EVENT_SCOPED_EXTRA }),
      entry('SURVEY_TEMPLATE_MANAGE', '共通テンプレートを管理', '全イベントで再利用するアンケートテンプレートを作成・更新できます。', { section: 'edit', sensitivity: 'private' }),
      entry('CUSTOMER_PROFILE_VIEW', '顧客候補を表示', '確認済み個人情報と既存顧客候補を表示できます。', { section: 'view', sensitivity: 'private' }),
      entry('CUSTOMER_PROFILE_EDIT', '顧客情報へ反映', '確認済み情報を顧客情報・イベント接点へ反映できます。', { section: 'edit', sensitivity: 'private' }),
      entry('CUSTOMER_PII_EXPORT', '個人情報付きXLSX', '個人情報を含むXLSXを出力できます。', { section: 'execute', sensitivity: 'system' }),
    ],
  },
  {
    key: 'events',
    title: 'イベント',
    items: [
      entry('EVENT_VIEW_PUBLISHED_LIST', '公開イベント一覧', '公開済みイベントを一覧で閲覧できます。', { section: 'view', ...EVENT_SCOPED_EXTRA }),
      entry('EVENT_VIEW_UNPUBLISHED_LIST', '未公開イベント一覧', '未公開イベントを一覧に含めます。', { section: 'view', ...EVENT_SCOPED_EXTRA }),
      entry('EVENT_VIEW_PUBLISHED_DETAIL', '公開イベント詳細', '公開済みイベントの詳細を閲覧できます。', { section: 'view', ...EVENT_SCOPED_EXTRA }),
      entry('EVENT_VIEW_UNPUBLISHED_DETAIL', '未公開イベント詳細', '未公開イベントの詳細を閲覧できます。', { section: 'view', ...EVENT_SCOPED_EXTRA }),
      entry('EVENT_VIEW_PUBLIC_STATUS', '公開状態', '公開/未公開状態を表示します。', { section: 'view', ...EVENT_SCOPED_EXTRA }),
      entry('EVENT_VIEW_PUBLISH_SCHEDULE', '公開予約日時', '公開予約日時を表示します。', { section: 'view', ...EVENT_SCOPED_EXTRA }),
      entry('EVENT_VIEW_CASTING_SETTINGS', 'キャスティング設定を表示', 'キャスティング設定パネルを表示します。', { section: 'view', ...EVENT_SCOPED_EXTRA }),
      entry('EVENT_VIEW_PARTICIPANTS', '参加アーティストを表示', '参加アーティスト一覧を表示します。', { section: 'view', ...EVENT_SCOPED_EXTRA, sensitivity: 'private' }),
      entry('EVENT_VIEW_TICKET_SALES', '券売状況を表示', '券売サマリーを表示します。', { section: 'view', ...EVENT_SCOPED_EXTRA, sensitivity: 'finance' }),
      entry('EVENT_VIEW_GUEST_TICKETS', '取り置き・招待を表示', '取り置き・招待の枠、登録者、利用状況を表示します。', { section: 'view', ...EVENT_SCOPED_EXTRA, sensitivity: 'private' }),
      entry('EVENT_VIEW_MARKETING', 'マーケティングを表示', 'イベントのマーケティング戦略・施策・実績・効果・学びを表示します。', { section: 'view', ...EVENT_SCOPED_EXTRA }),
      entry('EVENT_VIEW_EVENT_TASKS', 'イベントタスクを表示', 'イベント関連タスクを表示します。', { section: 'view', ...EVENT_SCOPED_EXTRA }),
      entry('EVENT_VIEW_TIMETABLE', 'タイムテーブルを表示', 'タイムテーブルを表示します。', { section: 'view', ...EVENT_SCOPED_EXTRA }),
      entry('EVENT_VIEW_MATERIALS', 'イベント資料を表示', 'イベント資料一覧を表示します。', { section: 'view', ...EVENT_SCOPED_EXTRA }),
      entry('EVENT_DOWNLOAD_MATERIALS', 'イベント資料をダウンロード', 'イベント資料ファイルをダウンロードできます。', { section: 'view', ...EVENT_SCOPED_EXTRA }),
      entry('EVENT_VIEW_MATERIAL_DOWNLOAD_LOGS', 'イベント資料DL履歴を表示', 'イベント資料のダウンロード履歴を表示します。', { section: 'view', ...EVENT_SCOPED_EXTRA }),
      entry('EVENT_VIEW_IMAGES', 'イベントアルバムを表示', 'イベントアルバムを表示します。', { section: 'view', ...EVENT_SCOPED_EXTRA }),
      entry('EVENT_DOWNLOAD_IMAGES', 'イベントアルバムをダウンロード', 'イベントアルバムの画像をダウンロードできます。', { section: 'view', ...EVENT_SCOPED_EXTRA }),
      entry('EVENT_VIEW_CONTRACT_STATUS', '契約状況を表示', '契約ステータスを表示します。', { section: 'view', ...EVENT_SCOPED_EXTRA, sensitivity: 'finance' }),
      entry('EVENT_CREATE', 'イベントを作成', 'イベント新規作成を実行できます。', { section: 'edit' }),
      entry('EVENT_EDIT_INFO', 'イベント基本情報を変更', 'イベント基本情報を更新できます。', { section: 'edit', ...EVENT_SCOPED_EXTRA }),
      entry('EVENT_EDIT_CASTING_SETTINGS', 'キャスティング設定を変更', 'キャスティング設定を更新できます。', { section: 'edit', ...EVENT_SCOPED_EXTRA }),
      entry('EVENT_EDIT_PARTICIPANTS', '参加者設定を変更', '参加者公開設定などを変更できます。', { section: 'edit', ...EVENT_SCOPED_EXTRA, sensitivity: 'private' }),
      entry('EVENT_EDIT_TICKET_SALES', '券売状況を変更', '券売状況を更新できます。', { section: 'edit', ...EVENT_SCOPED_EXTRA, sensitivity: 'finance' }),
      entry('EVENT_EDIT_GUEST_TICKETS', '取り置き・招待を変更', '取り置き・招待の登録、支払、来場、取消を更新できます。', { section: 'edit', ...EVENT_SCOPED_EXTRA, sensitivity: 'private' }),
      entry('EVENT_MANAGE_GUEST_TICKET_ALLOCATIONS', '取り置き・招待枠を管理', '参加者ごとの取り置き・招待枠と入力項目、通知を管理できます。', { section: 'edit', ...EVENT_SCOPED_EXTRA, sensitivity: 'private' }),
      entry('EVENT_SHARE_TICKET_SALES', '券売状況をアーティストへ共有', '券売枚数・残り枚数・販売率の共有範囲と通知を変更できます。', { section: 'edit', ...EVENT_SCOPED_EXTRA, sensitivity: 'private' }),
      entry('EVENT_EDIT_MARKETING', 'マーケティングを変更', 'イベントのマーケティング戦略・施策・実績・効果・学びを更新できます。', { section: 'edit', ...EVENT_SCOPED_EXTRA }),
      entry('EVENT_EDIT_EVENT_TASKS', 'イベントタスクを変更', 'イベントタスクを更新できます。', { section: 'edit', ...EVENT_SCOPED_EXTRA }),
      entry('EVENT_EDIT_TIMETABLE', 'タイムテーブルを変更', 'タイムテーブルを更新できます。', { section: 'edit', ...EVENT_SCOPED_EXTRA }),
      entry('EVENT_EDIT_MATERIALS', 'イベント資料を変更', 'イベント資料を更新できます。', { section: 'edit', ...EVENT_SCOPED_EXTRA }),
      entry('EVENT_EDIT_IMAGES', 'イベントアルバムを変更', 'イベントアルバムへの追加とフォルダ作成を実行できます。', { section: 'edit', ...EVENT_SCOPED_EXTRA }),
      entry('EVENT_DELETE', 'イベントを削除', 'イベント削除と削除可否確認を実行できます。', { section: 'edit', ...EVENT_SCOPED_EXTRA, sensitivity: 'danger' }),
      entry('event_cancel', 'イベント中止', 'イベント詳細から中止処理を実行できます。', { section: 'edit', sensitivity: 'danger' }),
    ],
  },
  {
    key: 'music',
    title: '楽曲管理',
    items: [
      entry('MUSIC_VIEW_LIBRARY', '楽曲情報を閲覧', '楽曲、人物、関連設定、楽譜セットのメタ情報を閲覧できます。', { section: 'view' }),
      entry('MUSIC_CREATE_WORK', '楽曲を作成', '楽曲正本を作成できます。', { section: 'edit' }),
      entry('MUSIC_EDIT_WORK', '楽曲を編集', '楽曲正本、年代、解説、人物クレジットを編集できます。', { section: 'edit' }),
      entry('MUSIC_DELETE_WORK', '楽曲をアーカイブ', '楽曲正本のアーカイブを実行できます。危険権限です。', { section: 'danger', sensitivity: 'danger' }),
      entry('MUSIC_CREATE_PERSON', '人物を作成', '作曲者、編曲者など人物正本を作成できます。', { section: 'edit' }),
      entry('MUSIC_EDIT_PERSON', '人物を編集', '人物の生没年、国、略歴、別名を編集できます。', { section: 'edit' }),
      entry('MUSIC_EDIT_RELATIONSHIPS', '関連設定を編集', '編曲元、変奏元、引用元など楽曲同士の関連設定を編集できます。', { section: 'edit' }),
      entry('MUSIC_UPLOAD_SCORE', '楽譜セットを登録', '楽譜セットのメタ情報と後続の楽譜ファイル登録を実行できます。', { section: 'edit', sensitivity: 'private' }),
      entry('MUSIC_DOWNLOAD_SCORE', '楽譜をダウンロード', '社内楽譜ファイルをダウンロードできます。', { section: 'download', sensitivity: 'private' }),
      entry('MUSIC_VIEW_RIGHTS', '権利情報を閲覧', '楽譜の権利状態、利用可能範囲、証跡メモを閲覧できます。', { section: 'view', sensitivity: 'private' }),
      entry('MUSIC_EDIT_RIGHTS', '権利情報を編集', '楽譜の権利状態、利用可能範囲、証跡メモを編集できます。', { section: 'edit', sensitivity: 'private' }),
      entry('MUSIC_UPLOAD_AUDIO', '音源を登録', '社内音源ファイルまたは音源リンクを登録できます。', { section: 'edit', sensitivity: 'private' }),
      entry('MUSIC_PLAY_AUDIO', '音源を再生', '社内音源ファイルを再生または確認できます。', { section: 'view', sensitivity: 'private' }),
      entry('MUSIC_SHARE_SCORE', '楽譜を共有', '共有レコードを作成してアーティストへ楽譜を共有できます。', { section: 'execute', sensitivity: 'private' }),
      entry('MUSIC_VIEW_SHARE_LOGS', '共有ログを閲覧', '楽譜共有とダウンロード履歴を閲覧できます。', { section: 'view', sensitivity: 'private' }),
      entry('MUSIC_VIEW_SOURCES', '出典を閲覧', '外部ID、出典、投入履歴、ライセンスメモを閲覧できます。', { section: 'view', sensitivity: 'private' }),
      entry('MUSIC_MERGE_ENTITIES', '楽曲・人物を統合', '重複した楽曲や人物を統合できます。危険権限です。', { section: 'danger', sensitivity: 'danger' }),
      entry('MUSIC_IMPORT_SEED_DATA', '初期データを投入', 'クラシック初期データのseed/importを実行できます。危険権限です。', { section: 'danger', sensitivity: 'system' }),
    ],
  },
  {
    key: 'artists',
    title: 'アーティスト',
    items: [
      entry('ARTIST_VIEW_LIST', 'アーティスト一覧', 'アーティスト一覧にアクセスできます。', { section: 'view', sensitivity: 'private', ...ARTIST_SCOPED_EXTRA }),
      entry('ARTIST_VIEW_PROFILE_BASIC', 'プロフィール基本情報', '基本プロフィールを閲覧できます。', { section: 'view', sensitivity: 'private', ...ARTIST_SCOPED_EXTRA }),
      entry('ARTIST_VIEW_AI_ANALYSIS', 'CasticartAI分析を閲覧', '公開情報に基づくCasticartAI分析と根拠を閲覧できます。', { section: 'view', sensitivity: 'private', ...ARTIST_SCOPED_EXTRA }),
      entry('ARTIST_VIEW_PROFILE_PRIVATE', 'プロフィール詳細', '詳細プロフィールを閲覧できます。', { section: 'view', sensitivity: 'private', ...ARTIST_SCOPED_EXTRA }),
      entry('ARTIST_VIEW_CONTACT', '連絡先', 'メールアドレス・電話番号を閲覧できます。', { section: 'view', sensitivity: 'private', ...ARTIST_SCOPED_EXTRA }),
      entry('ARTIST_VIEW_MEMO', '管理メモ', '管理用メモを閲覧できます。', { section: 'view', sensitivity: 'private', ...ARTIST_SCOPED_EXTRA }),
      entry('ARTIST_VIEW_BANK_INFO', '請求・口座情報', '銀行口座とインボイス情報を閲覧できます。', { section: 'view', sensitivity: 'finance', ...ARTIST_SCOPED_EXTRA }),
      entry('ARTIST_VIEW_WITHHOLDING', '源泉徴収設定', '源泉徴収設定を閲覧できます。', { section: 'view', sensitivity: 'finance', ...ARTIST_SCOPED_EXTRA }),
      entry('ARTIST_VIEW_CONTRACT_HISTORY', '契約履歴', '契約履歴を閲覧できます。', { section: 'view', sensitivity: 'finance', ...ARTIST_SCOPED_EXTRA }),
      entry('ARTIST_MANAGE_PHOTOS', '宣材写真を変更', '宣材写真を追加・削除できます。', { section: 'edit', sensitivity: 'private', ...ARTIST_SCOPED_EXTRA }),
      entry('ARTIST_DOWNLOAD_PHOTOS', '宣材写真をダウンロード', '宣材写真ファイルをダウンロードできます。', { section: 'export', sensitivity: 'private', ...ARTIST_SCOPED_EXTRA }),
      entry('ARTIST_MANAGE_GROUP_MEMBERSHIPS', 'グループ所属を変更', 'グループアカウントの所属メンバーを追加・解除できます。', { section: 'edit', sensitivity: 'private', ...ARTIST_SCOPED_EXTRA }),
      entry('ARTIST_MANAGE_AI_ANALYSIS', 'CasticartAI分析を補正', 'CasticartAI分析の再分析依頼と根拠の補正を実行できます。', { section: 'edit', sensitivity: 'private', ...ARTIST_SCOPED_EXTRA }),
      entry('ARTIST_EDIT_BANK_INFO', '請求・口座情報を変更', '銀行口座とインボイス情報を更新できます。', { section: 'edit', sensitivity: 'finance', ...ARTIST_SCOPED_EXTRA }),
      entry('ARTIST_EDIT_MEMO', '管理メモを変更', '管理用メモを更新できます。', { section: 'edit', sensitivity: 'private' }),
      entry('ARTIST_EDIT_WITHHOLDING', '源泉徴収設定を変更', '源泉徴収設定を更新できます。', { section: 'edit', sensitivity: 'finance' }),
      entry('CAN_BAN_ARTIST', 'アーティストBAN', 'アーティストのBAN/解除を行えます。', { section: 'danger', sensitivity: 'private' }),
    ],
  },
  {
    key: 'finance',
    title: '経理・損益',
    items: [
      entry('FINANCE_VIEW_DASHBOARD', '経理ダッシュボード閲覧', '経理ダッシュボードの年次集計と一覧を閲覧できます。', { section: 'view', sensitivity: 'finance' }),
      entry('FINANCE_VIEW_PAYMENT_LIST', '請求一覧閲覧', '月次支払い一覧を閲覧できます。', { section: 'view', sensitivity: 'finance', ...EVENT_SCOPED_EXTRA }),
      entry('FINANCE_VIEW_PAYMENT_DETAIL', '請求詳細閲覧', '請求書プレビューとダウンロード履歴を閲覧できます。', { section: 'view', sensitivity: 'finance', ...EVENT_SCOPED_EXTRA }),
      entry('FINANCE_VIEW_INVOICE_FILE', '請求書ファイル閲覧', '請求書PDFのプレビュー表示ができます。', { section: 'download', sensitivity: 'finance', ...EVENT_SCOPED_EXTRA }),
      entry('FINANCE_DOWNLOAD_INVOICE', '請求書ダウンロード', '請求書PDFの単体・一括ダウンロードができます。', { section: 'download', sensitivity: 'finance', ...EVENT_SCOPED_EXTRA }),
      entry('FINANCE_EXPORT_CSV', '経理CSV出力', '経理ダッシュボード、月次請求、年次請求明細のコピーとCSV出力ができます。', { section: 'download', sensitivity: 'finance' }),
      entry('FINANCE_VIEW_PL', 'イベント損益を閲覧', 'イベント損益の閲覧とサマリー確認ができます。', { section: 'view', sensitivity: 'finance' }),
      entry('FINANCE_EDIT_PL', 'イベント損益を編集', 'イベント損益の明細追加・更新・再計算ができます。', { section: 'edit', sensitivity: 'finance' }),
    ],
  },
  {
    key: 'tasks',
    title: 'タスク詳細操作',
    items: [
      entry('TASK_CREATE_ITEM', 'タスク作成', 'タスクとサブタスクを作成できます。', { section: 'edit' }),
      entry('TASK_EDIT_ITEM', 'タスク編集', 'タスク内容、並び順、依存関係を更新できます。', { section: 'edit' }),
      entry('TASK_DELETE_ITEM', 'タスク削除', 'タスクとサブタスクを削除できます。', { section: 'danger' }),
      entry('TASK_CREATE_SECTION', 'セクション作成', 'タスクセクションを作成できます。', { section: 'edit' }),
      entry('TASK_EDIT_SECTION', 'セクション編集', 'セクション名変更と並び替えができます。', { section: 'edit' }),
      entry('TASK_DELETE_SECTION', 'セクション削除', 'タスクセクションを削除できます。', { section: 'danger' }),
      entry('TASK_MANAGE_MEMBERS', 'タスクメンバー管理', 'タスクプロジェクトのメンバーと権限を変更できます。', { section: 'edit' }),
      entry('TASK_DELETE_PROJECT', 'タスクプロジェクト削除', 'タスクプロジェクトを削除できます。', { section: 'danger' }),
      entry('TASK_UPLOAD_ATTACHMENT', 'タスク添付アップロード', 'タスク添付ファイルをアップロードできます。', { section: 'edit' }),
      entry('TASK_DOWNLOAD_ATTACHMENT', 'タスク添付ダウンロード', 'タスク添付ファイルをダウンロードできます。', { section: 'download' }),
      entry('TASK_DELETE_ATTACHMENT', 'タスク添付削除', 'タスク添付ファイルを削除できます。', { section: 'danger' }),
      entry('TASK_MANAGE_TEMPLATES', 'タスクテンプレート管理', 'タスクテンプレートの閲覧・適用・作成ができます。', { section: 'execute' }),
      entry('TASK_IMPORT_ASANA', 'Asana CSV取り込み', 'タスクの CSV 取り込みを実行できます。', { section: 'execute' }),
    ],
  },
  {
    key: 'chat',
    title: 'チャットノート',
    items: [
      entry('EVENT_CHAT_VIEW_NOTES', 'イベントチャットノート閲覧', 'イベントチャット内のノート一覧と詳細を閲覧できます。', { section: 'view' }),
      entry('EVENT_CHAT_EDIT_NOTES', 'イベントチャットノート編集', 'イベントチャット内でノートの作成・更新・削除ができます。', { section: 'edit' }),
      entry('GROUP_CHAT_VIEW_NOTES', 'グループチャットノート閲覧', 'グループチャット内のノート一覧と詳細を閲覧できます。', { section: 'view' }),
      entry('GROUP_CHAT_EDIT_NOTES', 'グループチャットノート編集', 'グループチャット内でノートの作成・更新・削除ができます。', { section: 'edit' }),
      entry('PERSONAL_CHAT_VIEW_NOTES', '個別チャットノート閲覧', '個別チャット内のノート一覧と詳細を閲覧できます。', { section: 'view' }),
      entry('PERSONAL_CHAT_EDIT_NOTES', '個別チャットノート編集', '個別チャット内でノートの作成・更新・削除ができます。', { section: 'edit' }),
    ],
  },
  {
    key: 'system',
    title: 'システム',
    items: [
      entry('SYSTEM_VIEW_MANUALS', 'マニュアル', 'マニュアル画面へ入れます。', { section: 'view' }),
      entry('SYSTEM_EDIT_MANUALS', 'マニュアル編集', 'マニュアルの作成・更新・削除・公開ができます。', { section: 'edit', sensitivity: 'system' }),
      entry('CAN_VIEW_ACTION_LOGS', 'アクションログ閲覧', 'アクションログを閲覧できます。', { section: 'view', sensitivity: 'system' }),
      entry('CAN_EDIT_POLICIES', '規約管理', '規約管理を行えます。', { section: 'edit', sensitivity: 'system' }),
      entry('SYSTEM_SEND_OPERATIONAL_EMAILS', '運用連絡メール送信', 'アプデや不具合報告などの運用連絡メールを送信できます。', { section: 'execute', sensitivity: 'system' }),
      entry('SYSTEM_MANAGE_MOBILE_RELEASES', 'モバイル更新管理', 'モバイルアプリの更新案内と強制アップデート設定を管理できます。', { section: 'edit', sensitivity: 'system' }),
      entry('SYSTEM_MANAGE_MAC_RELEASES', 'Macアプリ更新管理', 'MacアプリDMGの登録、最新版化、旧版削除を管理できます。', { section: 'edit', sensitivity: 'system' }),
      entry('SYSTEM_MANAGE_ACCOUNTS', 'アカウント管理', '管理者アカウントを管理できます。', { section: 'danger', sensitivity: 'system' }),
      entry('SYSTEM_MANAGE_INVITES', '招待管理', '招待発行と停止済み管理を行えます。', { section: 'danger', sensitivity: 'system' }),
      entry('CAN_MANAGE_CONCIERGE', 'コンシェルジュ管理', 'コンシェルジュ申込の承認/否認を行えます。', { section: 'edit' }),
    ],
  },
]);

const ADMIN_PERMISSION_ITEMS = ADMIN_PERMISSION_GROUPS.flatMap((group) => group.items);
const ADMIN_PERMISSION_MAP = new Map(ADMIN_PERMISSION_ITEMS.map((item) => [item.key, item]));
const ADMIN_PERMISSION_KEY_SET = new Set(ADMIN_PERMISSION_ITEMS.map((item) => item.key));
// Pre-catalog grants supplement role defaults; showing them must preserve those defaults.
const LEGACY_ADDITIVE_PERMISSION_KEYS = new Set(['event_cancel']);
const TASK_ONLY_BLOCKED_KEYS = new Set(
  ADMIN_PERMISSION_ITEMS.map((item) => item.key).filter((key) => key.startsWith('EVENT_EDIT_'))
);

const DEFAULT_TEMPLATE_MAP = Object.freeze({
  [TEMPLATE_GENERAL_INTERNAL]: new Set(ADMIN_PERMISSION_ITEMS.filter((item) => !['event_cancel', 'CAN_VIEW_ACTION_LOGS', 'CAN_EDIT_POLICIES', 'SYSTEM_SEND_OPERATIONAL_EMAILS', 'SYSTEM_MANAGE_MOBILE_RELEASES', 'SYSTEM_MANAGE_MAC_RELEASES', 'SYSTEM_EDIT_MANUALS', 'SYSTEM_MANAGE_ACCOUNTS', 'SYSTEM_MANAGE_INVITES', 'CAN_MANAGE_CONCIERGE', 'NAV_ACTION_LOGS', 'NAV_OPERATIONAL_EMAILS', 'NAV_POLICIES', 'NAV_ACCOUNT_MANAGEMENT', 'NAV_CONCIERGE', 'NAV_AI_PLANNER', 'MUSIC_DELETE_WORK', 'MUSIC_MERGE_ENTITIES', 'MUSIC_IMPORT_SEED_DATA'].includes(item.key)).map((item) => item.key)),
  [TEMPLATE_TASK_ONLY_INTERNAL]: new Set([
    'NAV_EVENTS','NAV_EVENT_CHAT','NAV_GROUP_CHAT','NAV_PERSONAL_CHAT','NAV_TASKS',
    'EVENT_VIEW_PUBLISHED_LIST','EVENT_VIEW_PUBLISHED_DETAIL','EVENT_VIEW_EVENT_TASKS','EVENT_VIEW_TIMETABLE','EVENT_VIEW_MATERIALS','EVENT_VIEW_IMAGES','EVENT_DOWNLOAD_IMAGES',
    'EVENT_EDIT_EVENT_TASKS','TASK_CREATE_ITEM','TASK_EDIT_ITEM','TASK_DELETE_ITEM','TASK_CREATE_SECTION','TASK_EDIT_SECTION',
    'TASK_DELETE_SECTION','TASK_UPLOAD_ATTACHMENT','TASK_DOWNLOAD_ATTACHMENT','TASK_DELETE_ATTACHMENT',
    'EVENT_CHAT_VIEW_NOTES','EVENT_CHAT_EDIT_NOTES','GROUP_CHAT_VIEW_NOTES','GROUP_CHAT_EDIT_NOTES','PERSONAL_CHAT_VIEW_NOTES','PERSONAL_CHAT_EDIT_NOTES','SYSTEM_VIEW_MANUALS',
  ]),
  [TEMPLATE_TASK_ONLY_EXTERNAL]: new Set([
    'NAV_EVENTS','NAV_EVENT_CHAT','NAV_GROUP_CHAT','NAV_PERSONAL_CHAT','NAV_TASKS',
    'EVENT_VIEW_PUBLISHED_LIST','EVENT_VIEW_PUBLISHED_DETAIL','TASK_CREATE_ITEM','TASK_EDIT_ITEM','TASK_CREATE_SECTION',
    'TASK_EDIT_SECTION','TASK_UPLOAD_ATTACHMENT','TASK_DOWNLOAD_ATTACHMENT','SYSTEM_VIEW_MANUALS',
  ]),
  [TEMPLATE_EXTERNAL_GENERAL]: new Set([
    'NAV_EVENTS','NAV_EVENT_CHAT','NAV_GROUP_CHAT','NAV_PERSONAL_CHAT','NAV_TASKS',
    'EVENT_VIEW_PUBLISHED_LIST','EVENT_VIEW_PUBLISHED_DETAIL','TASK_CREATE_ITEM','TASK_EDIT_ITEM','TASK_CREATE_SECTION',
    'TASK_EDIT_SECTION','TASK_UPLOAD_ATTACHMENT','TASK_DOWNLOAD_ATTACHMENT','SYSTEM_VIEW_MANUALS',
  ]),
});

const normalizeRole = (value) => String(value || '').trim().toLowerCase() || 'general';

const normalizeScopeMode = (item, value) => {
  if (!item?.supportsScope) return SCOPE_ALL;
  const allowed = Array.isArray(item.scopeOptions) ? item.scopeOptions.map((option) => option.value) : [SCOPE_ALL];
  const normalized = String(value || '').trim().toLowerCase() || SCOPE_ALL;
  return allowed.includes(normalized) ? normalized : SCOPE_ALL;
};

const normalizeScopeTargets = (value) =>
  Array.isArray(value)
    ? Array.from(new Set(value.map((item) => String(item || '').trim()).filter(Boolean)))
    : [];

const normalizeConditions = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

export const normalizeAdminPermissionEntries = (rawEntries) => {
  if (!Array.isArray(rawEntries)) return [];
  const map = new Map();
  rawEntries.forEach((entryValue) => {
    const normalized =
      typeof entryValue === 'string'
        ? {
            permissionKey: entryValue.trim(),
            scopeMode: SCOPE_ALL,
            scopeTargets: [],
            conditions: {},
          }
        : entryValue && typeof entryValue === 'object'
          ? {
              permissionKey: String(entryValue.permissionKey || entryValue.permission || '').trim(),
              scopeMode: normalizeScopeMode(ADMIN_PERMISSION_MAP.get(String(entryValue.permissionKey || entryValue.permission || '').trim()), entryValue.scopeMode),
              scopeTargets: normalizeScopeTargets(entryValue.scopeTargets),
              conditions: normalizeConditions(entryValue.conditions),
            }
          : null;
    if (normalized?.permissionKey) map.set(normalized.permissionKey, normalized);
  });
  return Array.from(map.values());
};

export const normalizeAdminPermissionList = (permissions) =>
  normalizeAdminPermissionEntries(Array.isArray(permissions) ? permissions : [permissions])
    .map((entryItem) => entryItem.permissionKey);

export const resolveAdminPermissionTemplate = ({ role, isExternalAccount = false } = {}) => {
  const normalizedRole = normalizeRole(role);
  if (normalizedRole === 'admin') return TEMPLATE_ADMIN;
  if (normalizedRole === 'general') return TEMPLATE_GENERAL_INTERNAL;
  if (normalizedRole === 'task_only') {
    return isExternalAccount ? TEMPLATE_TASK_ONLY_EXTERNAL : TEMPLATE_TASK_ONLY_INTERNAL;
  }
  return TEMPLATE_EXTERNAL_GENERAL;
};

export const isAdminPermissionAllowedForRole = ({ role, permissionKey, isExternalAccount = false }) => {
  const normalizedRole = normalizeRole(role);
  const normalizedKey = String(permissionKey || '').trim();
  if (!normalizedKey) return false;
  if (isExternalAccount && EXTERNAL_ACCOUNT_ABSOLUTE_DENY_KEY_SET.has(normalizedKey)) {
    return false;
  }
  if (normalizedRole === 'admin') return true;
  if (normalizedRole === 'task_only' && TASK_ONLY_BLOCKED_KEYS.has(normalizedKey)) return false;
  return true;
};

export const buildEffectiveAdminPermissionStateMap = ({
  role,
  isExternalAccount = false,
  permissions = [],
  permissionEntries = null,
} = {}) => {
  const normalizedRole = normalizeRole(role);
  const explicitEntries = normalizeAdminPermissionEntries(permissionEntries || permissions);
  const explicitEntryMap = new Map(explicitEntries.map((entryItem) => [entryItem.permissionKey, entryItem]));
  const isAdmin = normalizedRole === 'admin' || explicitEntryMap.has('*') || explicitEntryMap.has('admin');
  const templateKey = resolveAdminPermissionTemplate({ role: normalizedRole, isExternalAccount });
  const templateSet = DEFAULT_TEMPLATE_MAP[templateKey] || new Set();
  const hasExplicitCatalog = explicitEntries.some((entryItem) =>
    ADMIN_PERMISSION_KEY_SET.has(entryItem.permissionKey) && !LEGACY_ADDITIVE_PERMISSION_KEYS.has(entryItem.permissionKey)
  );
  const stateMap = {};

  ADMIN_PERMISSION_ITEMS.forEach((item) => {
    if (isAdmin) {
      const adminAllowed = isAdminPermissionAllowedForRole({
        role: normalizedRole,
        permissionKey: item.key,
        isExternalAccount,
      });
      stateMap[item.key] = {
        permissionKey: item.key,
        enabled: adminAllowed,
        inherited: false,
        scopeMode: SCOPE_ALL,
        scopeTargets: [],
        conditions: {},
        meta: item,
      };
      return;
    }
    if (!isAdminPermissionAllowedForRole({ role: normalizedRole, permissionKey: item.key, isExternalAccount })) {
      stateMap[item.key] = { permissionKey: item.key, enabled: false, inherited: false, scopeMode: SCOPE_ALL, scopeTargets: [], conditions: {}, meta: item };
      return;
    }
    const sourceEntry = explicitEntryMap.get(item.key) || (!hasExplicitCatalog && templateSet.has(item.key)
      ? { permissionKey: item.key, scopeMode: SCOPE_ALL, scopeTargets: [], conditions: {} } : null);
    stateMap[item.key] = {
      permissionKey: item.key,
      enabled: Boolean(sourceEntry),
      inherited: Boolean(sourceEntry) && !hasExplicitCatalog && !explicitEntryMap.has(item.key),
      scopeMode: sourceEntry ? normalizeScopeMode(item, sourceEntry.scopeMode) : SCOPE_ALL,
      scopeTargets: sourceEntry ? normalizeScopeTargets(sourceEntry.scopeTargets) : [],
      conditions: sourceEntry ? normalizeConditions(sourceEntry.conditions) : {},
      meta: item,
    };
  });
  return stateMap;
};

export const buildEffectiveAdminPermissionMap = (options = {}) => {
  const stateMap = buildEffectiveAdminPermissionStateMap(options);
  return Object.fromEntries(Object.entries(stateMap).map(([key, value]) => [key, Boolean(value.enabled)]));
};

export const resolveAdminPermission = ({ permissionKey, ...options } = {}) =>
  Boolean(buildEffectiveAdminPermissionStateMap(options)[String(permissionKey || '').trim()]?.enabled);

export const resolveAdminPermissionConfig = ({ permissionKey, ...options } = {}) =>
  buildEffectiveAdminPermissionStateMap(options)[String(permissionKey || '').trim()] || null;

export const upsertAdminPermissionEntry = ({
  role,
  isExternalAccount = false,
  permissionEntries = [],
  permissionKey,
  enabled,
  scopeMode,
  scopeTargets,
  conditions,
} = {}) => {
  const normalizedKey = String(permissionKey || '').trim();
  if (!normalizedKey) return normalizeAdminPermissionEntries(permissionEntries);
  const currentEntries = normalizeAdminPermissionEntries(permissionEntries);
  const preservedEntries = currentEntries.filter((entryItem) => entryItem.permissionKey !== normalizedKey);
  if (!enabled || !isAdminPermissionAllowedForRole({ role, permissionKey: normalizedKey, isExternalAccount })) {
    return preservedEntries;
  }
  const item = ADMIN_PERMISSION_MAP.get(normalizedKey);
  preservedEntries.push({
    permissionKey: normalizedKey,
    scopeMode: normalizeScopeMode(item, scopeMode),
    scopeTargets: item?.supportsScope ? normalizeScopeTargets(scopeTargets) : [],
    conditions: item?.supportsScope ? normalizeConditions(conditions) : {},
  });
  return normalizeAdminPermissionEntries(preservedEntries);
};

export const applyAdminPermissionToggle = ({
  role,
  isExternalAccount = false,
  permissions = [],
  permissionEntries = null,
  permissionKey,
  enabled,
} = {}) =>
  upsertAdminPermissionEntry({
    role,
    isExternalAccount,
    permissionEntries: permissionEntries || permissions,
    permissionKey,
    enabled,
    scopeMode: resolveAdminPermissionConfig({
      role,
      isExternalAccount,
      permissionEntries: permissionEntries || permissions,
      permissionKey,
    })?.scopeMode || SCOPE_ALL,
    scopeTargets: resolveAdminPermissionConfig({
      role,
      isExternalAccount,
      permissionEntries: permissionEntries || permissions,
      permissionKey,
    })?.scopeTargets || [],
    conditions: resolveAdminPermissionConfig({
      role,
      isExternalAccount,
      permissionEntries: permissionEntries || permissions,
      permissionKey,
    })?.conditions || {},
  });
